import App from '@/App.vue'
import { ability } from '@/plugins/casl/ability'
import { userAuthState } from '@core/auth'
import { registerPlugins } from '@core/utils/plugins'
import { useTenantStore } from '@/stores/tenant'

// import * as Sentry from "@sentry/vue"
import { createPinia } from 'pinia'
import piniaPluginPersistedState from "pinia-plugin-persistedstate"

// import custom service worker
// eslint-disable-next-line import/no-unresolved
import { registerSW } from 'virtual:pwa-register'
import { createApp } from 'vue'

// import router
import { router } from '@/plugins/2.router/index'
import { isPwaEnabled } from '@/plugins/2.router/public-routes'


// Styles
import '@core/scss/template/index.scss'
import '@styles/styles.scss'

// Create vue app
const app = createApp(App)

const store = createPinia()

store.use(piniaPluginPersistedState)

app.use(store)

const auth = userAuthState()

if (localStorage.getItem('accessToken')){
  auth.fetchUser()
  ability.update(auth.user?.abilities)
}

registerPlugins(app)

;(async () => {
  // Resolve tenant before mounting
  const tenantStore = useTenantStore()

  const vuetifyInstance = app.config.globalProperties.$vuetify
  const { getI18n } = await import('@/plugins/i18n/index.js')
  const i18nInstance = getI18n()

  await tenantStore.resolve(vuetifyInstance, i18nInstance)

  if (tenantStore.errorStatus === 400) {
    await router.replace({
      name: 'invalid-url',
      params: {
        code: '400',
        title: 'Invalid Domain',
        description: 'This domain is not configured. Please check the URL and try again.',
      },
    })

    app.mount('#app')

    return
  }

  // check it is production or not
  // if (import.meta.env.PROD) {
  //   Sentry.init({
  //     app,
  //     dsn: "https://1f0cbc6dd6b61001d59623354843b607@o795642.ingest.us.sentry.io/4507213793984512",
  //     integrations: [
  //       Sentry.browserTracingIntegration({ router }),
  //       Sentry.replayIntegration(),
  //     ],

  //     // Set tracesSampleRate to 1.0 to capture 100%
  //     // of transactions for performance monitoring.
  //     // We recommend adjusting this value in production
  //     tracesSampleRate: 1.0,

  //     // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  //     denyUrls: ['localhost'],

  //     // Capture Replay for 10% of all sessions,
  //     // plus for 100% of sessions with an error
  //     replaysSessionSampleRate: 0.1,
  //     replaysOnErrorSampleRate: 1.0,
  //   })

  //   Sentry.setUser({
  //     id: auth?.user?.id, // User ID
  //     employee_code: auth?.user?.employee_code, // Employee code
  //     // Add other user properties if needed
  //   })
  // }


  const registerPWA = () => {
    if (!isPwaEnabled(window.location.pathname)) {
      console.log('PWA disabled for this route')

      return
    }

    const appVersion = __APP_VERSION__

    const updateSW = registerSW({
      onNeedRefresh() {
        const shouldUpdate = window.confirm(`New version (${appVersion}) available! Update now?`)
        if (shouldUpdate) {
          try {
            // Store the new version and update
            localStorage.setItem('sw_version', appVersion)
            updateSW(true).then(() => {
              window.location.reload()
            })
          } catch (e) {
            console.error('Error updating service worker:', e)
            window.location.reload()
          }
        } else {
          // ^ Remind user later if they decline better use on button click update
          setTimeout(() => {
            if (confirm('The update is still available. Would you like to update now?')) {
              updateSW(true).then(() => window.location.reload())
            }
          }, 5 * 60 * 1000) // Ask in every 5 minutes
        }
      },
      onOfflineReady() {
        console.log('App ready for offline use')
      },
      onRegistered(registration) {
        // Check version mismatch
        const currentVersion = localStorage.getItem('sw_version')
        if (currentVersion !== appVersion) {
          console.log(`Version mismatch (current: ${currentVersion}, new: ${appVersion})`)
          registration?.update()
        }
      },
      immediate: true,
    })

    // const updateSW = registerSW({
    //   onNeedRefresh() {
    //     const shouldUpdate = window.confirm('New version available! Update now?')
    //     if (shouldUpdate) {
    //       try {
    //         updateSW(true)
    //       } catch (e) {
    //         console.error('Error updating service worker:', e)
    //         window.location.reload()
    //       }
    //     }
    //   },
    //   onOfflineReady() {
    //     console.log('App ready for offline use')
    //   },
    //   immediate: false,
    // })
  }

  // Call this function when your app initializes
  registerPWA()

  // Provide auth and store to all components
  app.config.globalProperties.$auth = auth
  app.config.globalProperties.$store = store

  // structureClone support for older browsers window
  if (typeof structuredClone !== 'function') {
    console.log('structuredClone not supported')
    window.structuredClone = function (obj) {
      return JSON.parse(JSON.stringify(obj))
    }
  }

  // Mount vue app
  app.mount('#app')
})()
